import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import Select from "@/components/core/forms/select";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useCaseStageUpdateMutation } from "../api/queries/acitivities-query";
import { type CaseActivity, type CaseActivityForm, type CaseStageForm, caseActivityFormSchema, caseStageFormSchema } from "../types/activities";

export default function CaseStageUpdateModal({
	data,
	title,
}: {
	title: string;
	data: CaseActivity;
}) {
	const { closeModal } = useModalControl();
	const caseActivitiesMoveHearingMutation = useCaseStageUpdateMutation();

	const caseStageQuery = useSuspenseQuery(caseStagesQueryOptions());
	const caseStagesNew = caseStageQuery.data;
	const navigate = useNavigate();

	const form = useForm<CaseStageForm>({
		resolver: zodResolver(caseStageFormSchema),
		defaultValues: {
			id: data?.id,
			stage: data.stage,
		},
	});

	const handleSubmit = async (values: CaseStageForm) => {
		try {
			await caseActivitiesMoveHearingMutation.mutateAsync(values);
			await queryClient.invalidateQueries({ queryKey: ["case_records"] });
			await navigate({
				to: `/case-evaluation/${data?.id}`,
				search: (old) => ({
					...old,
					stage: form.getValues("stage"),
				}),
				replace: true,
			});
			closeModal();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-row items-center justify-between w-full py-3 px-4 border-b border-border/50">
					<span className="font-semibold text-foreground-dark text-base">Current Stage: {data.stage}</span>
				</div>
				<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2">
					<div className="col-span-12 w-full">
						<FormField
							control={form.control}
							name="stage"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Stage</FormLabel>
									</div>
									<FormGroup>
										<Select
											onValueChange={async (value) => {
												field.onChange(value);
											}}
											defaultValue={field.value}
											options={caseStagesNew.data
												.filter((c) => c.is_visible)
												.map((c) => ({
													label: c.label,
													value: c.name,
												}))}
										/>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>
				<div className="flex flex-row justify-between w-full gap-4 mt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" variant="default">
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
